/**
 * Generic Kanban toolbar — quiet chrome like production boards:
 * search + Filters popover + compact sort/group/density + primary actions.
 */

import type { ReactNode } from 'react';
import { Plus } from 'lucide-react';
import { Typography } from '../../atoms/Typography';
import { Tag } from '../../atoms/Tag';
import { Button } from '../../atoms/Button';
import { SearchInput } from '../../search-input';
import {
  kanbanBoardToolbarClass,
  kanbanViewsRowClass,
} from './Kanban.styles';
import { KanbanFilterSortControls } from './KanbanFilterSortControls';
import type {
  KanbanBulkAction,
  KanbanDensity,
  KanbanFilterDef,
  KanbanGroupOption,
  KanbanItemBase,
  KanbanSortOption,
  KanbanViewConfig,
} from './Kanban.config.types';

export interface KanbanToolbarProps<TItem extends KanbanItemBase> {
  showSearch?: boolean;
  searchQuery?: string;
  onSearchQueryChange?: (query: string) => void;
  searchPlaceholder?: string;

  showFilters?: boolean;
  filterDefs?: KanbanFilterDef<TItem>[];
  activeFilters?: Record<string, string | null>;
  onActiveFiltersChange?: (filters: Record<string, string | null>) => void;

  showSorting?: boolean;
  sortOptions?: KanbanSortOption<TItem>[];
  sortBy?: string | null;
  onSortByChange?: (sortBy: string | null) => void;

  showSwimlanes?: boolean;
  groupOptions?: KanbanGroupOption<TItem>[];
  groupBy?: string | null;
  onGroupByChange?: (groupBy: string | null) => void;

  showDensity?: boolean;
  density?: KanbanDensity;
  onDensityChange?: (density: KanbanDensity) => void;

  showViews?: boolean;
  views?: KanbanViewConfig[];
  activeViewId?: string | null;
  onActiveViewChange?: (viewId: string | null) => void;

  showBulkActions?: boolean;
  selectedCount?: number;
  bulkActions?: KanbanBulkAction[];
  onBulkAction?: (actionId: string) => void;

  showColumnManage?: boolean;
  onAddColumn?: () => void;

  visibleCount?: number;
  extra?: ReactNode;
  onClearFilters?: () => void;
  hasActiveFilters?: boolean;
}

const DENSITIES: KanbanDensity[] = ['compact', 'comfortable', 'detailed'];

export function KanbanToolbar<TItem extends KanbanItemBase>({
  showSearch,
  searchQuery = '',
  onSearchQueryChange,
  searchPlaceholder = 'Search…',
  showFilters,
  filterDefs = [],
  activeFilters = {},
  onActiveFiltersChange,
  showSorting,
  sortOptions = [],
  sortBy,
  onSortByChange,
  showSwimlanes,
  groupOptions = [],
  groupBy,
  onGroupByChange,
  showDensity,
  density = 'comfortable',
  onDensityChange,
  showViews,
  views = [],
  activeViewId,
  onActiveViewChange,
  showBulkActions,
  selectedCount = 0,
  bulkActions = [],
  onBulkAction,
  showColumnManage,
  onAddColumn,
  visibleCount,
  extra,
  onClearFilters,
}: KanbanToolbarProps<TItem>) {
  return (
    <div className="shrink-0 border-b border-dark-invirtus-soft bg-white">
      {showViews && views.length > 0 ? (
        <div className={kanbanViewsRowClass}>
          {views.map((view) => {
            const selected = activeViewId === view.id;
            return (
              <button
                key={view.id}
                type="button"
                className="rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue/40"
                onClick={() => onActiveViewChange?.(view.id)}
              >
                <Tag
                  intent={selected ? 'primary' : 'secondary'}
                  variant={selected ? 'solid' : 'subtle'}
                  size="sm"
                >
                  {view.label}
                </Tag>
              </button>
            );
          })}
        </div>
      ) : null}

      <div className={kanbanBoardToolbarClass}>
        {showSearch ? (
          <SearchInput
            value={searchQuery}
            onChange={(value) => onSearchQueryChange?.(value)}
            placeholder={searchPlaceholder}
            showClearButton
            size="sm"
            className="w-full max-w-[220px]"
          />
        ) : null}

        <KanbanFilterSortControls
          showFilters={showFilters}
          filterDefs={filterDefs}
          activeFilters={activeFilters}
          onActiveFiltersChange={onActiveFiltersChange}
          onClearFilters={onClearFilters}
          showSorting={showSorting}
          sortOptions={sortOptions}
          sortBy={sortBy}
          onSortByChange={onSortByChange}
        />

        {showSwimlanes && groupOptions.length > 0 ? (
          <label className="inline-flex items-center gap-2 h-8 rounded-lg border border-dark-invirtus-soft bg-white pl-2.5 pr-1.5 text-xs text-dark-invirtus/55">
            <span className="hidden sm:inline shrink-0">Group</span>
            <select
              className="h-7 min-w-[6.5rem] rounded-md border-0 bg-transparent pl-0.5 pr-1 text-sm text-dark-invirtus focus:outline-none focus:ring-0"
              value={groupBy ?? ''}
              onChange={(event) =>
                onGroupByChange?.(event.target.value || null)
              }
              aria-label="Group by"
            >
              <option value="">None</option>
              {groupOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        {showDensity ? (
          <div
            className="inline-flex items-center rounded-lg border border-dark-invirtus-soft p-0.5 bg-dark-invirtus-soft/20"
            role="group"
            aria-label="Card density"
          >
            {DENSITIES.map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => onDensityChange?.(value)}
                className={
                  density === value
                    ? 'px-2.5 py-1 rounded-md text-xs font-medium bg-white text-dark-invirtus shadow-sm'
                    : 'px-2.5 py-1 rounded-md text-xs font-medium text-dark-invirtus/55 hover:text-dark-invirtus'
                }
              >
                {value}
              </button>
            ))}
          </div>
        ) : null}

        <div className="flex items-center gap-2 ml-auto">
          {visibleCount != null ? (
            <Typography variant="body-xs" color="muted">
              {visibleCount} shown
            </Typography>
          ) : null}
          {extra}
          {showColumnManage ? (
            <Button
              type="contained"
              intent="secondary"
              size="sm"
              icon={<Plus className="h-3.5 w-3.5" />}
              label="Add column"
              onClick={onAddColumn}
            />
          ) : null}
        </div>
      </div>

      {showBulkActions && selectedCount > 0 ? (
        <div className="flex flex-wrap items-center gap-2 px-4 py-2 bg-blue/5 border-t border-dark-invirtus-soft">
          <Typography variant="body-xs" weight="semibold">
            {selectedCount} selected
          </Typography>
          {bulkActions.map((action) => (
            <Button
              key={action.id}
              type="outlined"
              intent={action.intent ?? 'secondary'}
              size="xs"
              label={action.label}
              onClick={() => onBulkAction?.(action.id)}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
